import React, { ReactElement } from "react";
import { GameState, LobbyState } from "../types";
import { isVictoryState } from "../utils";
import { getNextPresident, governmentFailed } from "./nextPresident";
import "./GameScreen.css";

type ActionPanelProps = {
  user: string;
  gameState: GameState;
  /** Status text maintained by the App's animation queue ("Waiting for ..."). */
  statusText: string;
  /** True when this user is the President and the term can be ended. */
  endTermReady: boolean;
  onEndTerm: () => void;
  /** True while a prompt/alert is open, so the panel does not compete with it. */
  alertOpen: boolean;
};

/** What the current user is being asked to do, in plain words. */
function myTask(game: GameState, user: string): string | undefined {
  const isPresident = game.president === user;
  switch (game.state) {
    case LobbyState.CHANCELLOR_NOMINATION:
      return isPresident ? "Pick a Chancellor to put on the ballot." : undefined;
    case LobbyState.CHANCELLOR_VOTING:
      return game.players[user]?.alive && !game.userVotes.hasOwnProperty(user)
        ? "Vote Ja or Nein on this government."
        : undefined;
    case LobbyState.LEGISLATIVE_PRESIDENT:
      return isPresident ? "Discard one of the three policies." : undefined;
    case LobbyState.LEGISLATIVE_CHANCELLOR:
      return game.chancellor === user ? "Enact one of the two policies." : undefined;
    case LobbyState.LEGISLATIVE_PRESIDENT_VETO:
      return isPresident ? "The Chancellor wants a veto. Agree or refuse." : undefined;
    case LobbyState.PP_PEEK:
      return isPresident ? "Peek at the next three policies." : undefined;
    case LobbyState.PP_INVESTIGATE:
      return isPresident ? "Investigate a player's party membership." : undefined;
    case LobbyState.PP_EXECUTION:
      return isPresident ? "Choose a player to execute." : undefined;
    case LobbyState.PP_ELECTION:
      return isPresident ? "Choose the next President." : undefined;
    default:
      return undefined;
  }
}

/** The "your move" / "waiting" panel beside the table, including the big End Term button. */
export function MainPanel(props: ActionPanelProps): ReactElement {
  const game = props.gameState;

  if (isVictoryState(game.state)) {
    const fascistWin =
      game.state === LobbyState.FASCIST_VICTORY_ELECTION || game.state === LobbyState.FASCIST_VICTORY_POLICY;
    return (
      <div className={"ap-panel ap-main ap-rise " + (fascistWin ? "ap-fascist" : "ap-liberal")}>
        <div className="ap-kicker">Game over</div>
        <div className="ap-text">{fascistWin ? "The Fascists have won." : "The Liberals have won."}</div>
      </div>
    );
  }

  if (props.endTermReady) {
    const nextPresident = getNextPresident(game);
    const last = game.history && game.history[game.history.length - 1];
    // What this term produced: usually a policy, but a failed vote or a veto enacts nothing.
    let card = game.lastPolicy === "FASCIST" ? "F" : "L";
    let kind = game.lastPolicy === "FASCIST" ? "fascist" : "liberal";
    let headline = kind === "fascist" ? "Fascist policy enacted" : "Liberal policy enacted";
    let detail = "Nothing else to do this term.";
    if (last && (last.outcome === "REJECTED" || last.outcome === "VETOED")) {
      card = "✗";
      kind = "neutral";
      headline = last.outcome === "REJECTED" ? "The vote failed" : "Agenda vetoed";
      detail = "No policy was enacted.";
    } else if (last && (last.outcome === "CHAOS_LIBERAL" || last.outcome === "CHAOS_FASCIST")) {
      kind = last.outcome === "CHAOS_FASCIST" ? "fascist" : "liberal";
      card = kind === "fascist" ? "F" : "L";
      headline = (kind === "fascist" ? "Fascist" : "Liberal") + " policy enacted by chaos";
      detail = "Three failed votes in a row.";
    }
    return (
      <div className="ap-panel ap-main ap-rise ap-endterm">
        <div className="ap-enacted">
          <div className={"ap-card ap-card-" + kind}>{card}</div>
          <div>
            <div className={"ap-kicker ap-kicker-" + kind}>{headline}</div>
            <div className="ap-text">{detail}</div>
          </div>
        </div>
        <button className="gold-button gold-button-large attention-button endterm-button" onClick={props.onEndTerm}>
          <span className="gold-sweep" />
          <span className="gold-label">END TERM &#8594;</span>
        </button>
        <div className="ap-hint">
          Everyone is waiting on you.
          {nextPresident ? " The presidency passes to " + nextPresident + "." : ""}
        </div>
      </div>
    );
  }

  const task = myTask(game, props.user);
  if (task) {
    return (
      <div className="ap-panel ap-main ap-yourmove">
        <div className="ap-kicker">Your move, {props.user}</div>
        <div className="ap-text">{task}</div>
        {!props.alertOpen && <div className="ap-hint">Opening your choices…</div>}
      </div>
    );
  }

  return (
    <div className="ap-panel ap-main">
      <div className="ap-kicker ap-kicker-muted">Waiting</div>
      <div className="ap-text">{props.statusText || "Waiting for the other players."}</div>
    </div>
  );
}

/** President and Chancellor in big type, plus who is next in turn order. */
function Government(props: { game: GameState }): ReactElement | null {
  const game = props.game;
  if (isVictoryState(game.state) || game.state === LobbyState.SETUP || !game.president) {
    return null;
  }
  const nominating = game.state === LobbyState.CHANCELLOR_NOMINATION;
  const voting = game.state === LobbyState.CHANCELLOR_VOTING;
  const next = getNextPresident(game);
  const failed = governmentFailed(game);
  const hasChancellor = !nominating && !failed && !!game.chancellor;
  const nomineeIsNext = hasChancellor && next === game.chancellor;
  return (
    <div className="ap-panel ap-gov">
      <div className="ap-gov-row ap-gov-president">
        <span className="ap-gov-role">President</span>
        <span className="ap-gov-name">{game.president}</span>
      </div>
      <div className="ap-gov-row ap-gov-chancellor">
        <span className="ap-gov-role">{voting ? "Chancellor nominee" : "Chancellor"}</span>
        {!hasChancellor ? (
          <span className="ap-gov-none">{failed ? "vote failed" : "not nominated yet"}</span>
        ) : (
          <span className="ap-gov-name">{game.chancellor}</span>
        )}
      </div>
      {next && (
        <div className={"ap-gov-next" + (nomineeIsNext ? " ap-gov-next-yes" : "")}>
          {nomineeIsNext
            ? game.chancellor + " becomes President next"
            : "Next in turn order: " + next}
        </div>
      )}
    </div>
  );
}

/** The side column: who is in government, then what the current player has to do. */
export default function ActionPanel(props: ActionPanelProps): ReactElement {
  return (
    <>
      <Government game={props.gameState} />
      <MainPanel {...props} />
    </>
  );
}
